import type { tInspectionFormInput } from '../types';
import type { ExpenseRecord } from '../types/costTrackingTypes';
import type { PMPartsAndProcedure } from '../types/pmTypes';
import type { NormalizedReportRecord } from '../types/reportTypes';
import { OPERATOR_RESPONSE_TYPES } from './constants';
import { isDailyServiceExpenseForReport } from './dailyServiceCostTracking';
import { parsePmPartsSnapshot } from './costTrackingUtils';
import { isEnteredQuantityValue, OPERATOR_RESPONSE_VALUES } from './formPromptMapping';
import {
    isReportUMCounterPromptLabel,
    parseInspectionReportPrompts,
} from './reportUtils';

/** Catalog fields used to estimate Item cost / UOM when expense snapshots omit them. */
export type UnitUsageCatalogItem = Pick<
    PMPartsAndProcedure,
    'id' | 'itemName' | 'price' | 'unitOfMeasure'
>;

export type UnitUsagePeriodPreset =
    | 'last-7-days'
    | 'last-30-days'
    | 'last-3-months'
    | 'last-6-months'
    | 'last-12-months'
    | 'ytd'
    | 'custom';

export type UnitUsageDateRange = {
    /** Inclusive calendar start YYYY-MM-DD */
    start: string;
    /** Inclusive calendar end YYYY-MM-DD */
    end: string;
};

export type UnitUsageItem = {
    key: string;
    name: string;
    quantity: number;
    unitOfMeasure?: string;
    cost: number;
};

export type UnitUsageTotals = {
    items: UnitUsageItem[];
    costTotal: number;
    reportCount: number;
    expenseCount: number;
};

export const UNIT_USAGE_PERIOD_OPTIONS: { id: UnitUsagePeriodPreset; label: string }[] = [
    { id: 'last-7-days', label: 'Last 7 days' },
    { id: 'last-30-days', label: 'Last 30 days' },
    { id: 'last-3-months', label: 'Last 3 months' },
    { id: 'last-6-months', label: 'Last 6 months' },
    { id: 'last-12-months', label: 'Last 12 months' },
    { id: 'ytd', label: 'YTD' },
    { id: 'custom', label: 'Custom' },
];

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

export function toCalendarDateString(value: Date): string {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
}

function startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function addMonths(date: Date, months: number): Date {
    // Clamp the day to the target month's last day so a month-end source day does
    // not overshoot into the following month (e.g. May 31 − 3mo must land in
    // February, not March 3). JS Date would otherwise normalize the overflow.
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth() + months;
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    const day = Math.min(date.getDate(), lastDayOfTargetMonth);
    return new Date(targetYear, targetMonth, day);
}

/**
 * Resolve an inclusive calendar date range for Usage period chips.
 */
export function resolveUnitUsageDateRange(
    preset: UnitUsagePeriodPreset,
    custom?: { start?: string; end?: string },
    now: Date = new Date(),
): UnitUsageDateRange {
    const today = startOfLocalDay(now);
    const end = toCalendarDateString(today);

    if (preset === 'custom') {
        const start = (custom?.start || end).trim() || end;
        const customEnd = (custom?.end || end).trim() || end;
        return start <= customEnd ? { start, end: customEnd } : { start: customEnd, end: start };
    }

    if (preset === 'ytd') {
        return { start: `${today.getFullYear()}-01-01`, end };
    }

    const daysBack: Record<Exclude<UnitUsagePeriodPreset, 'custom' | 'ytd'>, number> = {
        'last-7-days': 6,
        'last-30-days': 29,
        'last-3-months': 0,
        'last-6-months': 0,
        'last-12-months': 0,
    };

    if (preset === 'last-3-months') {
        return { start: toCalendarDateString(addMonths(today, -3)), end };
    }
    if (preset === 'last-6-months') {
        return { start: toCalendarDateString(addMonths(today, -6)), end };
    }
    if (preset === 'last-12-months') {
        return { start: toCalendarDateString(addMonths(today, -12)), end };
    }

    return {
        start: toCalendarDateString(addDays(today, -daysBack[preset])),
        end,
    };
}

export function calendarDateFromTimestamp(raw?: string | null): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    // A date-only string (YYYY-MM-DD with no time component) is already a calendar
    // date with no timezone — take it as-is. A full ISO timestamp carries a time
    // (and usually a zone), so slicing its first 10 chars would yield the UTC
    // calendar day, which can differ from the viewer's local day for evening
    // records. Parse those to a local Date so binning matches the locally-built
    // range bounds in resolveUnitUsageDateRange.
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return toCalendarDateString(parsed);
}

export function isCalendarDateInRange(
    calendarDate: string | null | undefined,
    range: UnitUsageDateRange,
): boolean {
    if (!calendarDate) return false;
    return calendarDate >= range.start && calendarDate <= range.end;
}

function normalizeItemName(name?: string | null): string {
    const trimmed = name?.trim();
    return trimmed || 'Consumable';
}

export function unitUsageItemKey(serviceItemId?: string | null, name?: string | null): string {
    const id = serviceItemId?.trim();
    if (id) return `item:${id}`;
    return `label:${normalizeItemName(name).toLowerCase()}`;
}

type MutableUsageItem = {
    key: string;
    name: string;
    quantity: number;
    unitOfMeasure?: string;
    cost: number;
};

function ensureItem(
    byKey: Map<string, MutableUsageItem>,
    key: string,
    name: string,
    unitOfMeasure?: string,
): MutableUsageItem {
    const existing = byKey.get(key);
    if (existing) {
        if (!existing.unitOfMeasure && unitOfMeasure) {
            existing.unitOfMeasure = unitOfMeasure;
        }
        if (existing.name === 'Consumable' && name !== 'Consumable') {
            existing.name = name;
        }
        return existing;
    }
    const created: MutableUsageItem = {
        key,
        name,
        quantity: 0,
        cost: 0,
        unitOfMeasure,
    };
    byKey.set(key, created);
    return created;
}

function isQuantityConsumablePrompt(prompt: tInspectionFormInput): boolean {
    if (isReportUMCounterPromptLabel(prompt.label)) return false;
    if (prompt.subType && prompt.subType !== OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY) {
        return false;
    }
    return isEnteredQuantityValue(prompt.value);
}

function isAcknowledgedProcedure(prompt: tInspectionFormInput): boolean {
    return (
        prompt.subType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT &&
        prompt.value === OPERATOR_RESPONSE_VALUES.acknowledged
    );
}

function addQuantityFromDailyServicePrompts(
    byKey: Map<string, MutableUsageItem>,
    prompts: tInspectionFormInput[],
) {
    for (const prompt of prompts) {
        if (isQuantityConsumablePrompt(prompt)) {
            const quantity = Number(prompt.value);
            if (!Number.isFinite(quantity) || quantity <= 0) continue;
            const name = normalizeItemName(prompt.label);
            const key = unitUsageItemKey(prompt.serviceItemId, name);
            const row = ensureItem(byKey, key, name, prompt.defaultUM || undefined);
            row.quantity += quantity;
            continue;
        }

        if (!isAcknowledgedProcedure(prompt)) continue;
        for (const line of prompt.procedureCostLines || []) {
            const quantity = Number(line.quantity) || 0;
            if (quantity <= 0) continue;
            const name = normalizeItemName(line.itemName);
            const key = unitUsageItemKey(line.serviceItemId, name);
            const row = ensureItem(byKey, key, name);
            row.quantity += quantity;
        }
    }
}

function expenseLinkedToDailyServiceReport(
    expense: Pick<ExpenseRecord, 'invoiceNumber' | 'workDescription'>,
    reportIds: Set<string>,
): boolean {
    for (const reportId of reportIds) {
        if (isDailyServiceExpenseForReport(expense, reportId)) {
            return true;
        }
    }
    return false;
}

function catalogUnitPrice(item: UnitUsageCatalogItem | null | undefined): number {
    return Number(item?.price) || 0;
}

/**
 * When Cost Tracking snapshots did not attach a line total, estimate Item cost
 * (and UOM) from the company service-item catalog. Does not overwrite snapshot costs.
 */
export function applyCatalogPricesToUsageItems(
    items: Iterable<MutableUsageItem>,
    catalog: UnitUsageCatalogItem[] | undefined,
): void {
    if (!catalog || catalog.length === 0) return;

    const byId = new Map<string, UnitUsageCatalogItem>();
    const byName = new Map<string, UnitUsageCatalogItem>();
    for (const item of catalog) {
        if (!item?.id) continue;
        byId.set(item.id, item);
        const nameKey = normalizeItemName(item.itemName).toLowerCase();
        if (nameKey && !byName.has(nameKey)) {
            byName.set(nameKey, item);
        }
    }

    for (const row of items) {
        const catalogItem = row.key.startsWith('item:')
            ? byId.get(row.key.slice('item:'.length))
            : byName.get(normalizeItemName(row.name).toLowerCase());
        if (!catalogItem) continue;

        if (!row.unitOfMeasure && catalogItem.unitOfMeasure) {
            row.unitOfMeasure = catalogItem.unitOfMeasure;
        }

        if (row.cost > 0 || row.quantity <= 0) continue;
        const unitPrice = catalogUnitPrice(catalogItem);
        if (unitPrice <= 0) continue;
        row.cost = row.quantity * unitPrice;
    }
}

/**
 * Roll up consumable quantities from Daily Service reports and dollar/qty lines
 * from Cost Tracking expenses without double-counting the same Daily Service submit.
 *
 * Item Cost prefers expense `pmPartsSnapshot` line totals; otherwise uses catalog
 * `price × quantity` when a matching service item is provided.
 */
export function buildUnitUsageTotals(args: {
    reports: Array<Pick<NormalizedReportRecord, 'id' | 'createdAt' | 'responseData' | 'unitId'> & {
        inspectionCompleted?: string | null;
    }>;
    expenses: ExpenseRecord[];
    range: UnitUsageDateRange;
    unitId?: string | null;
    /** Company service items — used to price Daily Service qty when snapshots omit dollars. */
    catalog?: UnitUsageCatalogItem[];
}): UnitUsageTotals {
    const { reports, expenses, range, unitId, catalog } = args;
    const byKey = new Map<string, MutableUsageItem>();
    const reportIdsInRange = new Set<string>();

    const reportsInRange = reports.filter((report) => {
        // When scoping to a unit, require an exact unitId match (orphan rows stay out).
        if (unitId && report.unitId !== unitId) return false;
        const day =
            calendarDateFromTimestamp(report.inspectionCompleted) ||
            calendarDateFromTimestamp(report.createdAt);
        return isCalendarDateInRange(day, range);
    });

    for (const report of reportsInRange) {
        reportIdsInRange.add(report.id);
        addQuantityFromDailyServicePrompts(
            byKey,
            parseInspectionReportPrompts(report.responseData),
        );
    }

    const expensesInRange = expenses.filter((expense) => {
        if (unitId && expense.unitId !== unitId) return false;
        const day = calendarDateFromTimestamp(expense.dateOfService);
        return isCalendarDateInRange(day, range);
    });

    let costTotal = 0;
    for (const expense of expensesInRange) {
        costTotal += Number(expense.totalExpense) || 0;
        const linkedToCountedReport = expenseLinkedToDailyServiceReport(expense, reportIdsInRange);
        const snapshots = parsePmPartsSnapshot(expense.pmPartsSnapshot);

        for (const line of snapshots) {
            const quantity = Number(line.quantity) || 0;
            const lineTotal = Number(line.lineTotal) || 0;
            const name = normalizeItemName(line.itemName);
            const key = unitUsageItemKey(line.serviceItemId, name);
            const row = ensureItem(byKey, key, name);

            if (!linkedToCountedReport && quantity > 0) {
                row.quantity += quantity;
            }
            if (lineTotal > 0) {
                row.cost += lineTotal;
            }
        }
    }

    applyCatalogPricesToUsageItems(byKey.values(), catalog);

    const items = [...byKey.values()]
        .filter((item) => item.quantity > 0 || item.cost > 0)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    return {
        items,
        costTotal,
        reportCount: reportsInRange.length,
        expenseCount: expensesInRange.length,
    };
}
